import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { BookkeepingAccount, BookkeepingAccountFilters } from '~/types/general-bookkeeping';
import { bookkeepingAccountLabel, checkBookkeepingAccountMatch } from '~/utils/bookkeeping/bookkeeping-account';
import { sortObjectsAlphabetically } from '~/utils/sort';
import SimpleComboBox, { SimpleComboboxOption } from './SimpleComboBox';

export interface BookkeepingAccountSelectProps extends BaseSelectProps {
  onChange: (value: string | null, account: BookkeepingAccount | null) => void;
  value: string | null;
  placeholder?: string;
  clearable?: boolean;
  filters?: BookkeepingAccountFilters;
}

function accountToOption(account: BookkeepingAccount): SimpleComboboxOption {
  return {
    label: bookkeepingAccountLabel(account),
    value: account.id,
    description: `${account.section.charAt(0).toUpperCase() + account.section.slice(1)} (${account.accountType})`,
  };
}

const BookkeepingAccountSelect = forwardRef<HTMLDivElement, BookkeepingAccountSelectProps>(
  ({ onChange, value, placeholder, clearable = true, filters = { hasChildren: false }, ...props }, ref) => {
    const { data: accounts } = useAllBookkeepingAccountsQuery(filters);
    const { t } = useTranslation('inputs', { keyPrefix: 'bookkeeping_account_select' });
    const displayedValueRef = useRef<string | null>(null);
    const [textInput, setTextInput] = useState<string>('');
    const [debouncedTextInput] = useDebounce(textInput, 150);

    const options = useMemo(() => {
      const accountOptions: SimpleComboboxOption[] = (accounts ?? [])
        .filter((account) => checkBookkeepingAccountMatch(account, debouncedTextInput))
        .slice(0, 200)
        .map(accountToOption)
        .sort(sortObjectsAlphabetically((item) => item.label));

      return accountOptions;
    }, [accounts, debouncedTextInput]);

    const handleChange = useCallback(
      (selectedValue: string | null) => {
        const selectedAccount = (accounts ?? []).find((account) => account.id === selectedValue) ?? null;
        setTextInput(selectedAccount ? bookkeepingAccountLabel(selectedAccount) : '');
        displayedValueRef.current = selectedValue;
        onChange(selectedValue, selectedAccount);
      },
      [accounts, onChange],
    );

    useEffect(() => {
      // Updates the displayed text input when the value is set programmatically
      if (!accounts || value === displayedValueRef.current) return;
      const account = value ? accounts.find((a) => a.id === value) : null;
      if (value && !account) return;
      setTextInput(account ? bookkeepingAccountLabel(account) : '');
      displayedValueRef.current = value;
    }, [accounts, value]);

    return (
      <SimpleComboBox
        {...props}
        ref={ref}
        onChange={handleChange}
        options={options}
        inputValue={textInput}
        value={value}
        className={props.className}
        placeholder={placeholder || t('select_account')}
        hasClearButton={clearable}
        onInputChange={setTextInput}
      />
    );
  },
);

BookkeepingAccountSelect.displayName = 'BookkeepingAccountSelect';

export default BookkeepingAccountSelect;
